import { useState } from 'react';
import { api, ServerError } from './api';
import { ExitCorner } from './exit';

/**
 * First run: this till has no identity yet.
 *
 * The installer finishes by telling the shopkeeper to pair the till from the
 * panel, and until now gave them no way to do it — pairing existed only as
 * `luma-store-agent --activate CODE` on a command line. So a customer install
 * ended on the ordinary sale screen, which claimed to be online and fully
 * synced, offered to open a shift, and failed with "clock not started".
 *
 * This screen takes over the whole window instead. Not a banner on the sale
 * screen: an unpaired till cannot sell, and a screen that lets someone start
 * ringing items into a device with no identity is a worse lie than the one it
 * replaces.
 */
export function SetupScreen({ onPaired }: { onPaired: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ shop: string; branch: string; device: string } | null>(null);

  const submit = async () => {
    const trimmed = code.trim();
    if (trimmed === '' || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ deviceCode: string; tenantName: string; branchName: string }>(
        '/setup/activate',
        { code: trimmed },
      );
      setDone({ shop: r.tenantName, branch: r.branchName, device: r.deviceCode });
      /*
       * Tell the app to start watching, and let it decide when we are done.
       *
       * This screen does NOT time itself out. It used to: a six second timer,
       * guessed to be longer than the agent's restart. It was not, the single
       * refetch behind it failed while the service was still coming up, and the
       * screen sat here forever on a till that had actually paired.
       *
       * The parent polls until the agent reports an identity and then unmounts
       * this screen. Waiting on the thing we are waiting for, rather than on a
       * number.
       */
      onPaired();
    } catch (e) {
      // The agent passes the server's own words through, and they are the
      // actionable part: "this code has already been used" tells someone what
      // to do next, "pairing failed" sends them hunting a network fault.
      setError(e instanceof ServerError ? e.message : 'جفت‌سازی انجام نشد — دوباره تلاش کنید');
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Frame>
        <div style={{ fontSize: 44, marginBottom: 6 }}>✓</div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 10px' }}>این صندوق جفت شد</h1>
        <div style={{ fontSize: 17, fontWeight: 700 }}>
          {[done.shop, done.branch].filter(Boolean).join(' — ') || 'آماده'}
        </div>
        {done.device && (
          <div className="pos-num" style={{ color: 'var(--luma-muted)', marginTop: 6 }}>
            کد دستگاه {done.device}
          </div>
        )}
        <div style={{ color: 'var(--luma-muted)', marginTop: 18, fontSize: 13, lineHeight: 1.9 }}>
          در حال آماده‌سازی… چند لحظه صبر کنید
          <br />
          {/* Said out loud because the agent restart takes longer than people
              expect, and a screen that looks frozen gets power-cycled — mid
              restart, which is the one moment that is genuinely bad. */}
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            صندوق در حال راه‌اندازی دوباره است و تا یک دقیقه ممکن است طول بکشد
          </span>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>راه‌اندازی صندوق</h1>
      <p style={{ color: 'var(--luma-muted)', margin: '0 0 22px', lineHeight: 1.9, fontSize: 13.5 }}>
        این صندوق هنوز به فروشگاهی وصل نشده است.
        <br />
        در پنل مدیریت، بخش «دستگاه‌ها»، یک کد فعال‌سازی بسازید و آن را اینجا وارد کنید.
      </p>

      <input
        autoFocus
        value={code}
        onChange={(e) => { setCode(e.target.value); setError(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
        placeholder="کد فعال‌سازی"
        disabled={busy}
        className="pos-num"
        style={{
          width: '100%', height: 56, textAlign: 'center',
          fontSize: 22, fontWeight: 700, letterSpacing: 2,
          border: `1px solid ${error ? 'var(--luma-danger, #C0392B)' : 'var(--luma-line, #D6D6CC)'}`,
          borderRadius: 10, background: 'var(--luma-card, #FFF)', outline: 'none',
        }}
      />

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.8,
            background: 'rgba(192,57,43,.08)', color: 'var(--luma-danger, #C0392B)',
          }}
        >
          {error}
        </div>
      )}

      <button
        onClick={() => void submit()}
        disabled={busy || code.trim() === ''}
        style={{
          width: '100%', height: 54, marginTop: 18, borderRadius: 10, border: 'none',
          fontSize: 16, fontWeight: 800, color: '#FFF',
          background: busy || code.trim() === '' ? 'var(--luma-placeholder, #B9B9AE)' : 'var(--luma-accent, #E8590C)',
          cursor: busy || code.trim() === '' ? 'default' : 'pointer',
        }}
      >
        {busy ? 'در حال جفت‌سازی…' : 'فعال‌سازی صندوق'}
      </button>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div
      dir="rtl"
      style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--luma-surface-desk, #F3F2EE)', padding: 24,
      }}
    >
      <div
        style={{
          width: 420, maxWidth: '100%', textAlign: 'center', padding: '34px 30px 30px',
          background: 'var(--luma-surface, #FFF)', borderRadius: 14,
          boxShadow: '0 2px 14px rgba(32,30,29,.10)',
        }}
      >
        <img src="logo.png" alt="" width={44} height={44} style={{ borderRadius: 12, marginBottom: 14 }} />
        {children}
      </div>
      {/* The window has no title bar: a screen with no way out is a trap, and
          this one can be the very first thing a shopkeeper ever sees. */}
      <ExitCorner />
    </div>
  );
}
